import { IScoutReviewRequest } from "../../services/alert/alert.service";
import { IFilterRequest } from "../../../modules/alerts/filter";
import { IFeature, FeatureType, FeatureUsageUnit } from "../feature.model";
import { IFeatureFamily, IModifyFeatureFamily } from "../feature-family.interface";
import { IBulkDistributionResponse, IBulkEditChanges, IBulkEditResponse, IBulkManageResponse, IBulkManagePair, IBulkTransitionResponse, ICameraLicenseInfo, ICameraLicenseLookupResponse, ICreateLicenseNoteRequest, IDistributionInstruction, IFamilyLicenseUsage, ILicenseLink, ILicenseNote, ILicenseUsage, ILicenseV2, ILicenseV2Request, IUsageLimitCheck } from "../license.interface";

export function mapFeatureResponse(response: any): IFeature {
    return {
        id: response.id,
        name: response.name,
        description: response.description,
        cost: response.cost,
        type: FeatureType[String(response.type).toUpperCase() as keyof typeof FeatureType],
        usageUnit: FeatureUsageUnit[String(response.usage_unit).toUpperCase() as keyof typeof FeatureUsageUnit],
        usageLimited: response.usage_limited,
        maxUsage: response.max_usage,
        dependencyIds: response.dependency_ids ?? [],
        licenseIds: response.license_ids ?? [],
        familyId: response.family_id ?? null,
        familyName: response.family_name ?? null,
        deprecated: response.deprecated ?? false,
    };
}

export function mapFeatureRequest(request: any): any {
    return {
        id: request.id,
        name: request.name,
        description: request.description,
        cost: request.cost,
        type: request.type != null ? String(request.type).toUpperCase() : undefined,
        usage_unit: request.usageUnit != null ? String(request.usageUnit).toUpperCase() : undefined,
        usage_limited: request.usageLimited,
        max_usage: request.maxUsage,
        dependency_ids: request.dependencyIds ?? [],
        family_id: request.familyId ?? null,
    };
}

export function mapLicenseV2Response(response: any): ILicenseV2 {
    if (response.status == null) {
        throw new Error('Invalid license response: missing status');
    }
    const normalizedStatus = String(response.status).toUpperCase();

    return {
        id: response.id,
        name: response.name,
        cost: response.cost,
        status: normalizedStatus as ILicenseV2['status'],
        organisationId: response.organisation_id,
        isDistributed: response.is_distributed,
        inUse: response.in_use,
        parentId: response.parent_id ?? null,
        featureIds: response.feature_ids ?? [],
        isValid: response.is_valid ?? false,
        activationTimestamp: response.activation_timestamp ?? null,
        cancellationTimestamp: response.cancellation_timestamp ?? null,
        cancellationRequestedTimestamp: response.cancellation_requested_timestamp ?? null,
    };
}

export function mapLicenseV2Request(request: ILicenseV2Request): any {
    return {
        id: request.id,
        name: request.name,
        cost: request.cost,
        status: request.status != null ? String(request.status).toUpperCase() : undefined,
        organisation_id: request.organisationId,
        is_distributed: request.isDistributed,
        in_use: request.inUse,
        parent_id: request.parentId ?? null,
        feature_ids: request.featureIds ?? [],
        quantity: request.quantity,
    };
}

export function mapDistributionInstructionRequest(instruction: IDistributionInstruction): any {
    const mapped: any = {
        child_org_id: instruction.childOrgId,
        quantity: instruction.quantity,
        cost_per_license: instruction.costPerLicense,
    };
    if (instruction.featureIds != null) {
        mapped.feature_ids = instruction.featureIds;
    }
    return mapped;
}

export function mapBulkDistributionResponse(response: any): IBulkDistributionResponse {
    return {
        results: (response.results ?? []).map((r: any) => ({
            childOrgId: r.child_org_id,
            status: r.status,
            licensesCreated: r.licenses_created ?? 0,
            data: (r.data ?? []).map(mapLicenseV2Response),
            error: r.error ?? null,
        })),
        summary: {
            totalInstructions: response.summary?.total_instructions ?? 0,
            succeeded: response.summary?.succeeded ?? 0,
            failed: response.summary?.failed ?? 0,
            totalLicensesCreated: response.summary?.total_licenses_created ?? 0,
        },
    };
}

export function mapBulkTransitionResponse(response: any): IBulkTransitionResponse {
    return {
        results: (response.results ?? []).map((r: any) => ({
            licenseId: r.license_id,
            status: r.status,
            data: r.data ? mapLicenseV2Response(r.data) : null,
            error: r.error ?? null,
        })),
        summary: {
            total: response.summary?.total ?? 0,
            succeeded: response.summary?.succeeded ?? 0,
            failed: response.summary?.failed ?? 0,
        },
    };
}

export function mapLicenseUsageResponse(response: any): ILicenseUsage {
    return {
        id: response.id,
        licenseId: response.license_id,
        featureId: response.feature_id,
        used: response.used,
    };
}

export function mapUsageLimitCheckResponse(response: any): IUsageLimitCheck {
    return {
        licenseId: response.license_id,
        featureId: response.feature_id,
        currentUsage: response.current_usage,
        maxUsage: response.max_usage,
        usageLimited: response.usage_limited,
        remaining: response.remaining ?? null,
        wouldExceed: response.would_exceed,
    };
}

export function mapLicenseLinkResponse(response: any): ILicenseLink {
    return {
        id: response.id,
        licenseId: response.license_id,
        entityType: response.entity_type,
        entityId: response.entity_id,
    };
}

export function mapBulkManagePairRequest(pair: IBulkManagePair): any {
    return {
        action: pair.action,
        license_id: pair.licenseId,
        entity_type: pair.entityType,
        entity_ids: pair.entityIds,
    };
}

export function mapBulkManageLinksResponse(response: any): IBulkManageResponse {
    return {
        results: (response.results ?? []).map((r: any) => ({
            action: r.action,
            licenseId: r.license_id,
            status: r.status,
            processed: r.processed ?? 0,
            skipped: r.skipped ?? 0,
            error: r.error ?? null,
        })),
        summary: {
            totalPairs: response.summary?.total_pairs ?? 0,
            succeeded: response.summary?.succeeded ?? 0,
            failed: response.summary?.failed ?? 0,
            totalProcessed: response.summary?.total_processed ?? 0,
            totalSkipped: response.summary?.total_skipped ?? 0,
        },
    };
}

export function mapLicenseNoteResponse(response: any): ILicenseNote {
    return {
        id: response.id,
        note: response.note,
        authorId: response.author_id,
        organisationId: response.organisation_id,
        creationTimestamp: response.creation_timestamp,
        deletionTimestamp: response.deletion_timestamp ?? null,
        licenseIds: response.license_ids ?? [],
    };
}

export function mapCreateLicenseNoteRequest(request: ICreateLicenseNoteRequest): any {
    const mapped: any = { note: request.note };
    if (request.licenseIds != null) {
        mapped.license_ids = request.licenseIds;
    }
    return mapped;
}

export function mapBulkEditChangesRequest(changes: IBulkEditChanges): any {
    const mapped: any = {};
    if (changes.cost != null) {
        mapped.cost = changes.cost;
    }
    if (changes.name != null) {
        mapped.name = changes.name;
    }
    if (changes.featureIds != null) {
        mapped.feature_ids = changes.featureIds;
    }
    return mapped;
}

export function mapBulkEditResponse(response: any): IBulkEditResponse {
    return {
        results: (response.results ?? []).map((r: any) => ({
            licenseId: r.license_id,
            status: r.status,
            error: r.error ?? null,
        })),
        summary: {
            total: response.summary?.total ?? 0,
            succeeded: response.summary?.succeeded ?? 0,
            failed: response.summary?.failed ?? 0,
        },
    };
}

export function mapCameraLicenseInfoResponse(response: any): ICameraLicenseInfo {
    return {
        id: response.id,
        name: response.name,
    };
}

export function mapCameraLicenseLookupResponse(response: any): ICameraLicenseLookupResponse {
    const cameraLicenses: Record<string, ICameraLicenseInfo[]> = {};
    const raw = response.camera_licenses ?? {};
    for (const [cameraId, licenses] of Object.entries(raw)) {
        cameraLicenses[cameraId] = (licenses as any[]).map(mapCameraLicenseInfoResponse);
    }
    return { cameraLicenses };
}

export function mapFamilyLicenseUsageResponse(response: any): IFamilyLicenseUsage {
    return {
        hasLicense: response.has_license,
        secondsUsed: response.seconds_used,
        secondsLimit: response.seconds_limit,
    };
}

export function mapScoutReviewFilterRequest(request: any, filters: IFilterRequest): IScoutReviewRequest {
    return {
        scoutType: request.scoutType,
        filters: filters,
        notes: request.notes
    }
}

export function mapFeatureFamilyResponse(response: any): IFeatureFamily {
    return {
        id: response.id,
        name: response.name,
        description: response.description ?? null,
        allowSiblings: response.allow_siblings ?? false,
    };
}

export function mapFeatureFamilyRequest(request: IModifyFeatureFamily): any {
    return {
        name: request.name,
        description: request.description,
        allow_siblings: request.allowSiblings,
    };
}
